import type { Block, Inline } from "@esi/shared";
import { parseMarkdown } from "@/components/markdown/parse";

/** A passage to place on the page: its quote and the context that picks its occurrence. */
export interface PassageAnchor {
  quote: string;
  prefix: string;
  suffix: string;
}

/**
 * Where a passage ends on the rendered page (wiki-collaboration-ux D15): the raw offset an inline
 * marker follows (null inside a code block, which takes none), and the top-level block a blocking
 * line goes under.
 */
export interface Placement {
  at: number | null;
  block: number;
}

interface Char {
  c: string;
  at: number | null;
  block: number;
}

/**
 * Places each anchor on `markdown` as the reading view renders it, without the DOM: the text
 * leaves in document order, whitespace ignored and struck ranges (`skip`, the Marked view's
 * removals) left out, as the selection reads them. The occurrence with the anchor's context wins,
 * else the first; null when the quote is not on the page (it then sits above the page).
 */
export function placeAnchors(
  markdown: string,
  anchors: readonly PassageAnchor[],
  skip: readonly { start: number; end: number }[] = [],
  omitBlock: number | null = null,
): (Placement | null)[] {
  if (!anchors.length) {
    return [];
  }
  const chars: Char[] = [];
  // Text leaves come in raw order: one cursor over the sorted struck ranges.
  const struck = [...skip].sort((a, b) => a.start - b.start);
  let cursor = 0;
  const skipped = (at: number) => {
    while (cursor < struck.length && (struck[cursor]?.end ?? 0) <= at) {
      cursor++;
    }
    const range = struck[cursor];
    return !!range && range.start <= at;
  };
  const push = (text: string, block: number, at: (i: number) => number | null) => {
    for (let i = 0; i < text.length; i++) {
      const c = text[i] as string;
      if (/\s/.test(c)) {
        continue;
      }
      chars.push({ c, at: at(i), block });
    }
  };
  const visit = (nodes: readonly Inline[], block: number, outer: number | null) => {
    for (const node of nodes) {
      switch (node.type) {
        case "text":
          for (let i = 0; i < node.text.length; i++) {
            const at = node.start + i;
            if (!skipped(at)) {
              push(node.text[i] as string, block, () => outer ?? at + 1);
            }
          }
          break;
        case "code":
          push(node.text, block, () => outer ?? node.end);
          break;
        case "wikilink":
          push(node.label, block, () => outer ?? node.end);
          break;
        case "strong":
        case "em":
          visit(node.children, block, outer);
          break;
        case "link":
          // A marker follows the whole link, never inside it.
          visit(node.children, block, outer ?? node.end);
          break;
        default:
          break;
      }
    }
  };
  const walk = (block: Block, index: number) => {
    switch (block.type) {
      case "heading":
      case "paragraph":
      case "quote":
        visit(block.content, index, null);
        break;
      case "code":
        push(block.text, index, () => null);
        break;
      case "list":
        for (const item of block.items) {
          visit(item.content, index, null);
          for (const child of item.children) {
            walk(child, index);
          }
        }
        break;
      case "table":
        for (const row of [block.header, ...block.rows]) {
          for (const cell of row) {
            visit(cell, index, null);
          }
        }
        break;
      default:
        break;
    }
  };
  parseMarkdown(markdown).forEach((block, index) => {
    if (index !== omitBlock) {
      walk(block, index);
    }
  });
  const text = chars.map((ch) => ch.c).join("");
  return anchors.map((anchor) => {
    const needle = anchor.quote.replace(/\s+/g, "");
    if (!needle) {
      return null;
    }
    const prefix = anchor.prefix.replace(/\s+/g, "");
    const suffix = anchor.suffix.replace(/\s+/g, "");
    let index = text.indexOf(needle);
    for (let i = index; i >= 0; i = text.indexOf(needle, i + 1)) {
      if (text.endsWith(prefix, i) && text.startsWith(suffix, i + needle.length)) {
        index = i;
        break;
      }
    }
    const last = index < 0 ? undefined : chars[index + needle.length - 1];
    return last ? { at: last.at, block: last.block } : null;
  });
}
